/** My saved signature (deputies and clerks) and notary commission (clerks). */
import { useState } from 'react';
import { api } from './api.ts';
import { SignaturePad } from './affidavit.tsx';
import { PasskeysCard } from './passkey.tsx';
import { Badge, Card, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from './ui.tsx';

export function useMySignature() {
  return useLoad(() => api.get<any>('/api/me/signature'), []);
}

export function MySignatureCard({ title = 'My signature' }: { title?: string }) {
  const r = useMySignature();
  const [drawing, setDrawing] = useState(false);
  const [img, setImg] = useState<string | null>(null);
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const d = r.data;
  const save = () =>
    a.run(async () => {
      await api.post('/api/me/signature', { imageDataUrl: img });
      setDrawing(false);
      setImg(null);
      r.reload();
    });
  return (
    <Card title={title} className="mysig">
      {!drawing && d.hasSignature && (
        <>
          <div className="mysig-img">
            <img src={d.imageDataUrl} alt="Your saved signature" />
          </div>
          <p className="small muted">Saved {new Date(d.updatedAt).toLocaleDateString()}. Used when you sign affidavits{d.canNotarize ? ' and in your notary block' : ''}.</p>
          <button className="btn" onClick={() => setDrawing(true)}>
            Redo my signature
          </button>
        </>
      )}
      {!drawing && !d.hasSignature && (
        <>
          <Notice tone="warn">You haven’t saved a signature yet. Save it once and it’s ready every time you sign an affidavit.</Notice>
          <button className="btn primary" onClick={() => setDrawing(true)}>
            Draw my signature
          </button>
        </>
      )}
      {drawing && (
        <div className="stack" data-unsaved={img ? 'true' : undefined}>
          <p className="small">Sign with your finger, a stylus or the mouse, the same way you sign on paper.</p>
          <SignaturePad onChange={setImg} />
          <div className="row gap">
            <button className="btn primary" onClick={save} disabled={!img || a.busy}>
              Save signature
            </button>
            <button className="btn ghost" onClick={() => (setDrawing(false), setImg(null))}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <ErrorBox error={a.error} />
    </Card>
  );
}

export function notaryTone(days: number): 'bad' | 'warn' | 'ok' {
  return days < 0 ? 'bad' : days <= 30 ? 'warn' : 'ok';
}

export function NotaryCard() {
  const r = useMySignature();
  const [name, setName] = useState<string | null>(null);
  const [expires, setExpires] = useState<string | null>(null);
  const [commissioned, setCommissioned] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const a = useAction();
  if (!r.data) return null;
  if (!r.data.canNotarize) return null;
  const n = r.data.notary;
  const nm = name ?? n?.name ?? '';
  const ex = expires ?? n?.expires ?? '';
  const cm = commissioned ?? n?.commissioned ?? '';
  const dirty = (name !== null && name !== (n?.name ?? '')) || (expires !== null && expires !== (n?.expires ?? '')) || (commissioned !== null && commissioned !== (n?.commissioned ?? ''));
  return (
    <Card title="Notary commission">
      {n ? (
        <p>
          <Badge tone={notaryTone(n.daysLeft)}>
            {n.daysLeft < 0 ? `Expired ${fmt(n.expires)}` : n.daysLeft === 0 ? 'Expires today' : `Expires ${fmt(n.expires)} · ${n.daysLeft} days left`}
          </Badge>
        </p>
      ) : (
        <Notice tone="warn">Enter your commission to notarize affidavits.</Notice>
      )}
      {n && (
        <p className="small">
          <strong>{n.name}</strong>, Notary Public for South Carolina{n.commissioned ? ` · commissioned ${fmt(n.commissioned)}` : ''} · expires {fmt(n.expires)}
        </p>
      )}
      {n && !n.commissioned && <Notice tone="warn">Add the date you were commissioned. It’s needed to keep notarizing.</Notice>}
      {n && n.daysLeft < 0 && <Notice tone="bad">You can’t notarize with an expired commission. Enter your new expiration date once it’s renewed.</Notice>}
      {n && n.daysLeft >= 0 && n.daysLeft <= 30 && <Notice tone="warn">Your commission expires soon. Renew it with the Secretary of State, then update the date here.</Notice>}
      <form
        className="stack"
        data-unsaved={dirty ? 'true' : undefined}
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            await api.post('/api/me/notary', { name: nm.trim(), commissioned: cm, expires: ex });
            setName(null);
            setExpires(null);
            setCommissioned(null);
            setSaved(true);
            r.reload();
          });
        }}
      >
        <Field label="Full name as it appears on your commission">
          <input value={nm} onChange={(e) => (setSaved(false), setName(e.target.value))} required autoComplete="name" placeholder="First Middle Last" />
        </Field>
        <div className="grid2">
          <Field label="Commission date">
            <input type="date" value={cm} onChange={(e) => (setSaved(false), setCommissioned(e.target.value))} required />
          </Field>
          <Field label="Commission expires">
            <input type="date" value={ex} onChange={(e) => (setSaved(false), setExpires(e.target.value))} required />
          </Field>
        </div>
        <div className="row gap">
          <button className="btn primary" disabled={a.busy || !nm.trim() || !ex || !cm || !dirty}>
            Save commission
          </button>
          {saved && <span className="ok-text">Saved.</span>}
        </div>
      </form>
      {!r.data.hasSignature && <p className="small muted">Also save your signature above. It goes in your notary block.</p>}
      <ErrorBox error={a.error} />
    </Card>
  );
}

const fmt = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
};

/** Office: My profile page. */
export function MyProfile() {
  return (
    <div className="stack narrow-page">
      <h1>My profile</h1>
      <PasskeysCard />
      <MySignatureCard />
      <NotaryCard />
    </div>
  );
}
